import { useRouter } from '@tanstack/react-router';
import { useCallback, useSyncExternalStore } from 'react';
import { localiseHref, localiseHtmlHrefs } from './localised-href';
import type { UrlLanguage } from './url-language';

const unsubscribeNothing = () => undefined;
const noSubscription = () => unsubscribeNothing;

/**
 * The language of the address (`router.urlLanguage`), re-rendering when it changes; `undefined` outside a
 * router (a component rendered on its own in a test) or while the address names no language.
 */
function useUrlLanguage() {
  const router = useRouter({ warn: false }) as { urlLanguage?: UrlLanguage } | undefined;
  const urlLanguage = router?.urlLanguage;
  const current = () => urlLanguage?.current();

  return useSyncExternalStore(urlLanguage?.subscribe ?? noSubscription, current, current);
}

/**
 * Localises the internal hrefs of plain <a>s for the language of the address, the way the router's rewrite
 * does for <Link>s: `localise('/compatibility')` is `/de/kompatibilitaet` under a /de/ address. External,
 * hash-only, mailto: and tel: hrefs come back as they are; so does everything while the address names no
 * language (dev server, tests at internal paths) or outside a router.
 */
export function useLocalisedHref(): (href: string) => string {
  const language = useUrlLanguage();

  return useCallback((href: string) => localiseHref(language, href), [language]);
}

/** As useLocalisedHref, for the hrefs inside an HTML string (translated FAQ answers). */
export function useLocalisedHtml(): (html: string) => string {
  const language = useUrlLanguage();

  return useCallback((html: string) => localiseHtmlHrefs(language, html), [language]);
}
